import { useState } from "react";
import { Link } from "../components/LocaleLink";
import {
  Mic, Mail, MapPin, Building2, Users, Globe2, Download, Copy, Check, Newspaper,
  Quote, ArrowRight, ChevronRight, FileText,
} from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { CONTACT } from "../i18n/translations";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { JsonLd, buildBreadcrumbSchema } from "../components/JsonLd";

const origin = () =>
  typeof window !== "undefined" ? window.location.origin : "https://velmiraliving.com";

const ASSET_LINKS = ["/logo-velmira.jpg", "/press", "/riviera-real-estate-data-2026"];

const overline = "font-accent text-xs sm:text-sm uppercase tracking-[0.25em] text-primary";

export default function Press() {
  const { lang, t } = useLang();
  const [copiedBio, setCopiedBio] = useState(false);

  const url = `${origin()}/${lang}/press`;
  const breadcrumb = [
    { name: t("blogUi.home"), url: `${origin()}/${lang}` },
    { name: t("press.breadcrumb"), url },
  ];

  const boilerplate = t("press.boiler");
  const facts = t("press.facts");
  const expertise = t("press.expertise");
  const quotes = t("press.quotes");
  const assets = t("press.assets");

  const copyBoilerplate = async () => {
    try {
      await navigator.clipboard.writeText(boilerplate);
      setCopiedBio(true);
      setTimeout(() => setCopiedBio(false), 1800);
    } catch (err) {
      console.warn("Clipboard copy failed:", err);
    }
  };

  return (
    <div data-testid="press-page" className="pt-32 md:pt-40 pb-24 md:pb-32">
      <Seo title={t("press.seoTitle")} description={t("press.seoDesc")} />
      <JsonLd id="press-breadcrumb" data={buildBreadcrumbSchema(breadcrumb)} />

      <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1100px]">
        {/* Breadcrumb */}
        <nav
          aria-label="Breadcrumb"
          data-testid="press-breadcrumb"
          className="flex items-center gap-2 font-accent text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-8"
        >
          <Link to="/" className="hover:text-primary">{t("blogUi.home")}</Link>
          <ChevronRight size={12} />
          <span className="text-primary">{t("press.breadcrumb")}</span>
        </nav>

        {/* HERO */}
        <Reveal>
          <p className={overline}>
            <Newspaper size={13} strokeWidth={1.6} className="inline mr-2 -mt-0.5" />
            {t("press.overline")}
          </p>
          <h1 className="mt-5 font-headings text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight text-foreground max-w-3xl">
            {t("press.title")}
          </h1>
          <p className="mt-8 font-body font-light text-lg text-foreground/75 leading-relaxed max-w-2xl">
            {t("press.intro")}
          </p>
        </Reveal>

        {/* CONTACT */}
        <Reveal delay={0.1}>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            <a
              href="mailto:press@velmiraliving.com?subject=Press%20enquiry"
              data-testid="press-contact-email"
              className="group border border-primary/40 bg-secondary p-8 flex items-start gap-4 hover:border-primary transition-colors"
            >
              <Mail size={22} className="text-primary shrink-0 mt-1" strokeWidth={1.5} />
              <div>
                <p className="font-accent text-[11px] uppercase tracking-[0.2em] text-primary/80">
                  {t("press.emailLabel")}
                </p>
                <p className="mt-2 font-headings text-xl text-foreground group-hover:text-primary transition-colors">
                  press@velmiraliving.com
                </p>
                <p className="mt-2 font-body font-light text-sm text-foreground/70">
                  {t("press.emailNote")}
                </p>
              </div>
            </a>
            <a
              href={`${CONTACT.whatsappHref}?text=${encodeURIComponent("Hi Velmira — press enquiry")}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="press-contact-whatsapp"
              className="group border border-border/60 bg-secondary p-8 flex items-start gap-4 hover:border-primary transition-colors"
            >
              <Mic size={22} className="text-primary shrink-0 mt-1" strokeWidth={1.5} />
              <div>
                <p className="font-accent text-[11px] uppercase tracking-[0.2em] text-primary/80">
                  {t("press.interviewsLabel")}
                </p>
                <p className="mt-2 font-headings text-xl text-foreground group-hover:text-primary transition-colors">
                  {CONTACT.whatsapp}
                </p>
                <p className="mt-2 font-body font-light text-sm text-foreground/70">
                  {t("press.interviewsNote")}
                </p>
              </div>
            </a>
          </div>
        </Reveal>

        {/* COMPANY FACTS */}
        <Reveal>
          <section className="mt-20">
            <div className="flex items-end justify-between gap-6 mb-8">
              <div>
                <p className={overline}>
                  <Building2 size={13} strokeWidth={1.6} className="inline mr-2 -mt-0.5" />
                  {t("press.factsOverline")}
                </p>
                <h2 className="mt-4 font-headings text-3xl sm:text-4xl text-foreground">
                  {t("press.factsTitle")}
                </h2>
              </div>
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-border/50 border border-border/50">
              {facts.map((f, i) => (
                <div key={i} data-testid={`press-fact-${i}`} className="bg-secondary p-6">
                  <dt className="font-accent text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
                    {f.label}
                  </dt>
                  <dd className="mt-2 font-headings text-lg text-foreground">{f.value}</dd>
                </div>
              ))}
            </dl>
          </section>
        </Reveal>

        {/* BOILERPLATE */}
        <Reveal>
          <section className="mt-20">
            <div className="flex items-center justify-between gap-4 mb-6">
              <div>
                <p className={overline}>{t("press.boilerOverline")}</p>
                <h2 className="mt-4 font-headings text-3xl sm:text-4xl text-foreground">
                  {t("press.boilerTitle")}
                </h2>
              </div>
              <button
                type="button"
                onClick={copyBoilerplate}
                data-testid="press-copy-boilerplate"
                className="inline-flex items-center gap-2 border border-primary/50 hover:border-primary text-primary px-4 py-2 uppercase tracking-[0.2em] text-[10px] font-body transition-colors"
              >
                {copiedBio ? <Check size={13} /> : <Copy size={13} />}
                {copiedBio ? t("press.copied") : t("press.copy")}
              </button>
            </div>
            <blockquote
              data-testid="press-boilerplate"
              className="border-l-2 border-primary/50 pl-6 py-2 font-body font-light text-lg text-foreground/85 leading-relaxed"
            >
              {boilerplate}
            </blockquote>
          </section>
        </Reveal>

        {/* EXPERTISE */}
        <Reveal>
          <section className="mt-20">
            <p className={overline}>
              <Users size={13} strokeWidth={1.6} className="inline mr-2 -mt-0.5" />
              {t("press.expOverline")}
            </p>
            <h2 className="mt-4 font-headings text-3xl sm:text-4xl text-foreground">
              {t("press.expTitle")}
            </h2>
            <ul className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              {expertise.map((e, i) => (
                <li
                  key={i}
                  data-testid={`press-expertise-${i}`}
                  className="border border-border/60 bg-secondary p-6 hover:border-primary/50 transition-colors"
                >
                  <h3 className="font-headings text-xl text-foreground leading-tight">
                    {e.title}
                  </h3>
                  <p className="mt-3 font-body font-light text-sm text-foreground/70 leading-relaxed">
                    {e.desc}
                  </p>
                </li>
              ))}
            </ul>
          </section>
        </Reveal>

        {/* QUOTES */}
        <Reveal>
          <section className="mt-20">
            <p className={overline}>
              <Quote size={13} strokeWidth={1.6} className="inline mr-2 -mt-0.5" />
              {t("press.quotesOverline")}
            </p>
            <h2 className="mt-4 font-headings text-3xl sm:text-4xl text-foreground">
              {t("press.quotesTitle")}
            </h2>
            <p className="mt-4 font-body font-light text-foreground/70 max-w-2xl">
              {t("press.quotesIntro")}
            </p>
            <div className="mt-8 space-y-6">
              {quotes.map((q, i) => (
                <figure
                  key={i}
                  data-testid={`press-quote-${i}`}
                  className="border border-border/60 bg-card p-7 md:p-8"
                >
                  <Quote size={20} className="text-primary mb-4" strokeWidth={1.5} />
                  <blockquote className="font-headings text-lg md:text-xl leading-relaxed text-foreground/90 italic">
                    &ldquo;{q.quote}&rdquo;
                  </blockquote>
                  <figcaption className="mt-5 pt-4 border-t border-border/40 font-accent text-[11px] uppercase tracking-[0.25em] text-primary/85">
                    Velmira Living — {q.context}
                  </figcaption>
                </figure>
              ))}
            </div>
          </section>
        </Reveal>

        {/* DOWNLOADABLE ASSETS */}
        <Reveal>
          <section className="mt-20">
            <p className={overline}>
              <Download size={13} strokeWidth={1.6} className="inline mr-2 -mt-0.5" />
              {t("press.assetsOverline")}
            </p>
            <h2 className="mt-4 font-headings text-3xl sm:text-4xl text-foreground">
              {t("press.assetsTitle")}
            </h2>
            <ul className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
              {assets.map((a, i) => (
                <li key={i}>
                  <a
                    href={ASSET_LINKS[i]}
                    data-testid={`press-asset-${i}`}
                    className="group block border border-border/60 bg-secondary p-6 h-full hover:border-primary transition-colors"
                  >
                    <FileText size={20} className="text-primary" strokeWidth={1.5} />
                    <p className="mt-4 font-headings text-lg text-foreground group-hover:text-primary transition-colors leading-tight">
                      {a.label}
                    </p>
                    <p className="mt-2 font-body font-light text-sm text-foreground/70">
                      {a.note}
                    </p>
                  </a>
                </li>
              ))}
            </ul>
          </section>
        </Reveal>

        {/* LOCATION */}
        <Reveal>
          <section className="mt-20">
            <p className={overline}>
              <Globe2 size={13} strokeWidth={1.6} className="inline mr-2 -mt-0.5" />
              {t("press.locOverline")}
            </p>
            <h2 className="mt-4 font-headings text-3xl sm:text-4xl text-foreground">
              {t("press.locTitle")}
            </h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border border-border/60 bg-secondary p-7">
                <MapPin size={20} className="text-primary" strokeWidth={1.5} />
                <p className="mt-4 font-headings text-xl text-foreground">{t("press.niceTitle")}</p>
                <p className="mt-2 font-body font-light text-sm text-foreground/70 leading-relaxed">
                  {t("press.niceDesc")}
                </p>
              </div>
              <div className="border border-border/60 bg-secondary p-7">
                <MapPin size={20} className="text-primary" strokeWidth={1.5} />
                <p className="mt-4 font-headings text-xl text-foreground">{t("press.monacoTitle")}</p>
                <p className="mt-2 font-body font-light text-sm text-foreground/70 leading-relaxed">
                  {t("press.monacoDesc")}
                </p>
              </div>
            </div>
          </section>
        </Reveal>

        {/* CTA */}
        <Reveal>
          <div
            data-testid="press-cta"
            className="mt-20 border border-primary/30 bg-secondary p-10 md:p-14 text-center"
          >
            <h2 className="font-headings text-3xl sm:text-4xl leading-tight text-foreground">
              {t("press.ctaTitle")}
            </h2>
            <p className="mt-4 font-body font-light text-foreground/75 max-w-xl mx-auto">
              {t("press.ctaText")}
            </p>
            <a
              href="mailto:press@velmiraliving.com?subject=Press%20enquiry"
              data-testid="press-cta-email"
              className="mt-8 inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-10 py-4 uppercase tracking-[0.2em] text-xs font-body transition-all"
            >
              {t("press.ctaButton")} <ArrowRight size={15} />
            </a>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
